import React, { FC, useCallback, JSX } from 'react';

import { WithAlloySuspense } from '@adsk/alloy-react-suspense';

import AttachmentsList, { type AttachmentsListProps } from './AttachmentsList';
import { hasPreview, getAttachmentType } from '../../api/attachments';
import { DOWNLOAD_METHODS, VIEWER_TYPES } from '../../consts';
import { useAttachmentsContext } from '../../context/AttachmentsContext';
import { useTmpLocalViews } from '../../context/LocalViewsProvider';
import {
  Attachment,
  AttachmentDraft,
  AttachmentListVariant,
} from '../../types';
import { useAttachmentsViewerContext } from '../AttachmentsViewer/context';

type ConnectedAttachmentsListProps = {
  renderEmptyState?: () => JSX.Element | null;
  openAttachmentWith?: (attachment: Attachment) => void;
} & AttachmentsListProps;

const ConnectedAttachmentsList: FC<ConnectedAttachmentsListProps> = ({
  renderEmptyState,
  openAttachmentWith,
  ...props
}) => {
  const {
    attachments,
    drafts,
    loading,
    deleteAttachment,
    deleteDraft,
    showLoadMore,
    onLoadMore,
    retryDraftUpload,
    downloadAttachment,
    openPDFAttachmentInNewTab,
    errorLoadingAttachments,
    onRetryLoadingAttachments,
    contextualTrack,
    allowDelete,
    readOnly,
    onAttachmentClick: onAttachmentClickFromProp,
  } = useAttachmentsContext();

  const { openViewer } = useAttachmentsViewerContext();
  const { localViews } = useTmpLocalViews();

  const onAttachmentClick = useCallback(
    (attachment: Attachment) => {
      if (onAttachmentClickFromProp) {
        onAttachmentClickFromProp(attachment);
      }

      if (openAttachmentWith && typeof openAttachmentWith === 'function') {
        openAttachmentWith(attachment);
        contextualTrack('prodex.attachments.open_in_viewer', {
          file_type: attachment.fileType,
          viewer: VIEWER_TYPES.EXTERNAL,
        });
        return;
      }

      if (hasPreview(attachment)) {
        openViewer(attachment.attachmentId);
        contextualTrack('prodex.attachments.open_in_viewer', {
          file_type: attachment.fileType,
          viewer: VIEWER_TYPES.INTERNAL,
        });
      } else {
        const isPDF = getAttachmentType(attachment.fileName) === 'pdf';

        if (isPDF) {
          void openPDFAttachmentInNewTab({
            attachmentId: attachment.attachmentId,
          });
        } else {
          void downloadAttachment({
            attachmentId: attachment.attachmentId,
            method: DOWNLOAD_METHODS.CLICK,
          });
        }
      }
    },
    [
      onAttachmentClickFromProp,
      openAttachmentWith,
      contextualTrack,
      openViewer,
      openPDFAttachmentInNewTab,
      downloadAttachment,
    ],
  );

  const onDraftClick = useCallback(
    (draft: AttachmentDraft) => {
      const viewUrl = localViews[draft.attachmentId];
      if (viewUrl) {
        contextualTrack('prodex.attachments.open_new_tab.start', {
          file_type: draft.file.type,
          draft: true,
          method: 'click',
        });

        window.open(localViews[draft.attachmentId], '_blank');
        contextualTrack('prodex.attachments.open_new_tab.complete', {
          file_type: draft.file.type,
          draft: true,
          method: 'click',
          result: 'success',
        });
      }
    },
    [contextualTrack, localViews],
  );

  const onVariantToggle = useCallback(
    (variant: AttachmentListVariant) => {
      contextualTrack('prodex.attachments.list.switch_view', {
        selected_view: variant,
      });
    },
    [contextualTrack],
  );

  return (
    <AttachmentsList
      attachments={attachments}
      drafts={drafts}
      loading={loading}
      onDeleteAttachment={deleteAttachment}
      onDeleteDraft={deleteDraft}
      onRetryDraft={retryDraftUpload}
      renderEmptyState={renderEmptyState}
      allowDelete={allowDelete && !readOnly}
      showLoadMore={showLoadMore}
      onLoadMore={onLoadMore}
      onDraftClick={onDraftClick}
      onAttachmentClick={onAttachmentClick}
      onDownloadAttachment={(attachmentId) =>
        downloadAttachment({
          attachmentId,
          method: DOWNLOAD_METHODS.DEFAULT,
        })
      }
      error={errorLoadingAttachments}
      onRetryLoadingAttachments={onRetryLoadingAttachments}
      onVariantToggle={onVariantToggle}
      {...props}
    />
  );
};

export default WithAlloySuspense(ConnectedAttachmentsList);
